/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState, useMemo, useEffect } from "react";
import { HiOutlineUser } from "react-icons/hi";
import {
  PageLayout,
  TableSkeleton,
  VehicleModal,
  ParameterModal,
  EmptyState,
  SuccessPopup,
  emptyVehicleIllustration,
  VehicleList,
  VehicleCategories,
  InventoryCatalogHeader,
  CatalogDeleteConfirmDialog,
} from "@components";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useCache } from "../../context/CacheContext";
import { useParameters, useCatalogViewMode } from "../../hooks";
import { useWorkspaceData } from "@data";
import { useTranslation } from "react-i18next";
import { IVehicle, ParameterType } from "../../types";
import { Company, Vehicle, Parameter } from "../../models";
import { useSearchParams, useNavigate } from "react-router-dom";

export const VehiclesPage: FC = function () {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isUpsertModalOpen, openUpsertModal] = useState(false);
  const [isDeleteModalOpen, openDeleteModal] = useState(false);
  const [isCategoryModalOpen, setCategoryModalOpen] = useState(false);
  const [vehicleCategories, setVehicleCategories] = useState([] as Parameter[]);
  const [workspaceCompanies, setWorkspaceCompanies] = useState([] as Company[]);
  const [isLoading, setLoading] = useState(true);
  const [splitIsOpen, setSplitIsOpen] = useState(false);
  const [vehicleData, setVehicleData] = useState(Vehicle.default());
  const { viewMode, setViewMode } = useCatalogViewMode("grid");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [vehicleSaveError, setVehicleSaveError] = useState<string | null>(null);

  // Sjekk om det finnes en reg-parameter i URL-en og sett visningsmodusen til liste hvis den finnes
  useEffect(() => {
    const regParam = searchParams.get("reg");
    if (regParam) {
      setViewMode("list");
      setSplitIsOpen(true);
    }
  }, [searchParams, setViewMode]);

  const { activeWorkspace } = useWorkspace();
  const { updateCacheKey } = useCache();
  // Vehicles and companies come from the centrally-loaded store.
  const { vehicles, companies, loading } = useWorkspaceData();
  const categoriesQuery = useParameters(undefined, ParameterType.VehicleType);
  const { t } = useTranslation(["common", "inventory", "vehicles"]);

  const isQueryLoading = useMemo(
    () =>
      loading.vehicles ||
      loading.companies ||
      categoriesQuery.isLoading ||
      categoriesQuery.isRefetching,
    [loading.vehicles, loading.companies, categoriesQuery],
  );

  const categories = useMemo(
    () => categoriesQuery.data ?? [],
    [categoriesQuery],
  );

  useEffect(() => {
    if (!isQueryLoading) {
      setVehicleCategories(categories);
    }
  }, [isQueryLoading, categories]);

  useEffect(() => {
    if (!isQueryLoading) {
      setWorkspaceCompanies(companies);
    }
  }, [isQueryLoading, companies]);

  useEffect(() => {
    if (!isQueryLoading) {
      setLoading(false);
    }
  }, [isQueryLoading, vehicles]);

  // Check for 'new' query parameter to open vehicle creation modal
  useEffect(() => {
    const newParam = searchParams.get("new");
    if (newParam === "true" && !isQueryLoading && categories.length > 0) {
      setVehicleData(Vehicle.default());
      openUpsertModal(true);
    }
  }, [searchParams, isQueryLoading, categories]);

  const handleSelectVehicle = (vehicle: Vehicle) => {
    setVehicleSaveError(null);
    setVehicleData(vehicle);

    if (Vehicle.isNew(vehicle)) {
      // For new vehicles, open the upsert modal directly
      openUpsertModal(true);
    } else if (viewMode === "grid") {
      openUpsertModal(true);
    } else {
      setSplitIsOpen(true);
    }
  };

  const handleCreateVehicle = () => {
    setVehicleSaveError(null);
    setVehicleData(Vehicle.default());
    openUpsertModal(true);
    navigate("?new=true", { replace: true });
  };

  const handleAddCategory = () => {
    setCategoryModalOpen(true);
  };

  const handleDeleteCategory = (categoryId: string) => {
    // Finn kategorien som skal slettes
    const categoryToDelete = categories.find((c) => c.id === categoryId);
    if (!categoryToDelete) return;

    // Slett kategorien direkte uten bekreftelsesdialog (bekreftelsen skjer allerede i modal)
    Parameter.remove(categoryToDelete);
    updateCacheKey();
    setLoading(true);

    // Hvis den slettede kategorien var valgt, gå tilbake til "alle"
    if (selectedCategoryId === categoryId) {
      setSelectedCategoryId(null);
    }
  };

  const handleVehicleCreated = () => {
    setShowSuccessPopup(true);
  };

  const handleAddDriver = () => {
    setShowSuccessPopup(false);
    navigate("/resources/drivers?new=true");
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
  };

  return (
    <PageLayout>
      {isLoading ? (
        <TableSkeleton />
      ) : vehicles.length === 0 ? (
        // Render empty state when there are no vehicles
        <EmptyState
          icon={emptyVehicleIllustration}
          title={t("vehicles:empty_state.title")}
          description={t("vehicles:empty_state.description")}
          buttonText={t("vehicles:empty_state.button_text")}
          buttonClick={handleCreateVehicle}
        />
      ) : (
        <div className="space-y-4">
          {/* Header with view toggle */}
          <InventoryCatalogHeader
            title={t("vehicles:title")}
            description={t("vehicles:description")}
            helpStorySlug="logibud-tms-web/help-popups/vehicle"
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            gridViewTitle={t("vehicles:view.grid")}
            listViewTitle={t("vehicles:view.list")}
            createButtonText={t("vehicles:new_vehicle")}
            onCreate={handleCreateVehicle}
          />

          {/* Content based on view mode */}
          {viewMode === "grid" ? (
            <div className="p-4">
              <VehicleCategories
                vehicles={vehicles}
                categories={vehicleCategories}
                isLoading={isQueryLoading}
                onSelectCategory={setSelectedCategoryId}
                onSelectVehicle={handleSelectVehicle}
                selectedCategoryId={selectedCategoryId}
                onAddCategory={handleAddCategory}
                onDeleteCategory={handleDeleteCategory}
              />
            </div>
          ) : (
            <VehicleList
              vehicles={vehicles}
              isLoading={isQueryLoading}
              companies={companies}
              categories={vehicleCategories}
              isOpen={setSplitIsOpen}
              createOrUpdate={(formData: Vehicle) => {
                setVehicleData(formData);
                openUpsertModal(true);
              }}
            />
          )}
        </div>
      )}

      {/* Vehicle Create/Edit Modal */}
      <VehicleModal
        data={vehicleData}
        workspaceId={activeWorkspace?.workspaceId}
        saveError={vehicleSaveError}
        cateogries={vehicleCategories}
        companies={workspaceCompanies}
        isShowing={isUpsertModalOpen}
        onConfirm={async (formData: IVehicle) => {
          try {
            setVehicleSaveError(null);
            const isNewVehicle = Vehicle.isNew(vehicleData);
            if (isNewVehicle) {
              const created = await Vehicle.create(
                activeWorkspace?.workspaceId ?? "",
                formData,
              );
              if (!created) {
                setVehicleSaveError(t("inventory:vehicle_save_failed"));
                return;
              }
              handleVehicleCreated();
            } else {
              await Vehicle.update(vehicleData, formData);
            }
          } catch (error) {
            setVehicleSaveError(t("inventory:vehicle_save_failed"));
            return;
          }
          openUpsertModal(false);
          updateCacheKey();
          setLoading(true);
          // Remove query parameter when modal closes
          if (searchParams.get("new") === "true") {
            navigate(window.location.pathname, { replace: true });
          }
        }}
        onCancel={() => {
          setVehicleSaveError(null);
          openUpsertModal(false);
          // Remove query parameter when modal closes
          if (searchParams.get("new") === "true") {
            navigate(window.location.pathname, { replace: true });
          }
        }}
      />

      {/* Vehicle Delete Confirmation */}
      <CatalogDeleteConfirmDialog
        title={
          t("inventory:delete_vehicle") +
          " " +
          vehicleData.registrationNumber +
          "?"
        }
        show={isDeleteModalOpen}
        onToggle={() => openDeleteModal(false)}
        onConfirm={() => {
          Vehicle.remove(vehicleData);
          openDeleteModal(false);
          updateCacheKey();
          setLoading(true);
        }}
      />

      {/* Category Create Modal */}
      <ParameterModal
        data={Parameter.default()}
        predefinedType={ParameterType.VehicleType}
        isShowing={isCategoryModalOpen}
        onConfirm={(formData) => {
          Parameter.create(activeWorkspace?.workspaceId ?? "", formData);
          setCategoryModalOpen(false);
          updateCacheKey();
          setLoading(true);
        }}
        onCancel={() => setCategoryModalOpen(false)}
      />

      <SuccessPopup
        isOpen={showSuccessPopup}
        title={t("vehicles:success.title")}
        message={t("vehicles:success.message")}
        primaryAction={{
          text: t("vehicles:success.add_driver"),
          onClick: handleAddDriver,
          variant: "success",
          icon: <HiOutlineUser className="mr-2 h-5 w-5" />,
        }}
        secondaryAction={{
          text: t("vehicles:success.not_now"),
          onClick: handleCloseSuccessPopup,
          variant: "secondary",
        }}
        onClose={handleCloseSuccessPopup}
      />
    </PageLayout>
  );
};
